import { contentLang } from './market'
/** One entry of `GET /v1/sports`. */
export interface SportSummary {
  slug: string
  label: string
  hasSchedule: boolean
  hasLeagues: boolean
  hasStandings: boolean
  hasOdds: boolean
}

declare global {
  interface Window {
    /** Host sites override the API origin globally instead of per element. */
    __SPORTS_API_BASE__?: string
  }
  /**
   * Baked in at build time by Vite's `define` from the SPORTS_API_BASE env var,
   * so one source tree produces a stage bundle and a prod bundle. Left
   * undefined outside a Vite build (unit tests), hence the `typeof` guard.
   */
  var __SPORTS_API_BASE_DEFAULT__: string | undefined
}

/**
 * Falls back to stage so a bundle built without the env var is merely pointed
 * at the wrong environment rather than broken — and so `pnpm dev` needs no
 * setup. Production builds MUST pass SPORTS_API_BASE; the prod CI job does.
 */
const DEFAULT_API_BASE =
  (typeof __SPORTS_API_BASE_DEFAULT__ !== 'undefined' && __SPORTS_API_BASE_DEFAULT__) ||
  'https://sports-api.stage.betbrothers.football'

/**
 * Precedence: the element's own `api-base` attribute, then a page-wide
 * `window.__SPORTS_API_BASE__`, then the compiled-in default. A host site
 * embedding several widgets sets the global once; a page mixing environments
 * overrides per element.
 */
export function resolveApiBase(attr?: string | null): string {
  const base = attr?.trim() || window.__SPORTS_API_BASE__?.trim() || DEFAULT_API_BASE
  return base.replace(/\/+$/, '')
}

export class SportsApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
    this.name = 'SportsApiError'
  }
}

export class SportsApiClient {
  constructor(private readonly baseUrl: string) {}

  /**
   * `signal` is not optional in practice: an attribute change can fire while a
   * previous request is still open, and without cancellation the slower reply
   * can land last and overwrite fresher data.
   */
  private async get<T>(path: string, signal?: AbortSignal): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      signal,
      headers: { accept: 'application/json' },
    })
    if (!response.ok) {
      throw new SportsApiError(
        `Request to ${path} failed with ${response.status}`,
        response.status,
      )
    }
    return (await response.json()) as T
  }

  sports(signal?: AbortSignal): Promise<SportSummary[]> {
    return this.get<SportSummary[]>('/v1/sports', signal)
  }

  livescores<T = unknown>(sport: string, day = 0, signal?: AbortSignal): Promise<T> {
    const query = day === 0 ? '' : `?day=${day}`
    return this.get<T>(`/v1/${encodeURIComponent(sport)}/livescores${query}`, signal)
  }

  standings<T = unknown>(sport: string, leagueId: string, signal?: AbortSignal): Promise<T> {
    return this.get<T>(
      `/v1/${encodeURIComponent(sport)}/standings/${encodeURIComponent(leagueId)}`,
      signal,
    )
  }

  /**
   * Every stored table of a sport whose standings are imported rather than a
   * live feed — cricket's series points tables (`{sport, tables:[{id,name,rows}]}`).
   * A single table is `standings(sport, id)` as for any other sport.
   */
  standingsList<T = unknown>(sport: string, signal?: AbortSignal): Promise<T> {
    return this.get<T>(`/v1/${encodeURIComponent(sport)}/standings`, signal)
  }

  /** Unified league standings ({leagueName, rows[]}) — football (v2) + basketball
   *  (basket-API) via `/v1/:sport/leagues/:id/standings`. */
  leagueStandings<T = unknown>(
    sport: string,
    leagueId: string,
    lang: string,
    signal?: AbortSignal,
  ): Promise<T> {
    return this.get<T>(
      `/v1/${encodeURIComponent(sport)}/leagues/${encodeURIComponent(leagueId)}/standings?lang=${encodeURIComponent(contentLang(lang))}`,
      signal,
    )
  }

  leagues<T = unknown>(sport: string, signal?: AbortSignal): Promise<T> {
    return this.get<T>(`/v1/${encodeURIComponent(sport)}/leagues`, signal)
  }

  /**
   * Raw feed passthrough inside the sport's tree — the API validates the path.
   * This is what race widgets use for season schedules (`f1-results`,
   * `motogp-schedule`), which the generic livescores route does not cover.
   */
  feed<T = unknown>(sport: string, path: string, signal?: AbortSignal): Promise<T> {
    return this.get<T>(
      `/v1/${encodeURIComponent(sport)}/feed?path=${encodeURIComponent(path)}`,
      signal,
    )
  }

  /** Resolved outright market for a locale: rows + brand + odds format. */
  outright<T = unknown>(key: string, lang: string, signal?: AbortSignal): Promise<T> {
    return this.get<T>(
      `/v1/outrights/${encodeURIComponent(key)}?lang=${encodeURIComponent(contentLang(lang))}`,
      signal,
    )
  }

  /** F1 / MotoGP Grands Prix: schedule, qualifying, results, race-winner odds. */
  raceCenter<T = unknown>(sport: string, lang: string, signal?: AbortSignal): Promise<T> {
    return this.get<T>(
      `/v1/race-center/${encodeURIComponent(sport)}?lang=${encodeURIComponent(contentLang(lang))}`,
      signal,
    )
  }

  /** Widget UI labels `{key: value}` for a language (CMS over shipped defaults). */
  uiLabels(lang: string, signal?: AbortSignal): Promise<Record<string, string>> {
    return this.get<Record<string, string>>(
      `/v1/ui?lang=${encodeURIComponent(contentLang(lang))}`,
      signal,
    )
  }

  /** Brand (logo + affiliate link) and odds format for a locale/context. */
  bookmaker<T = unknown>(lang: string, context = 'default', signal?: AbortSignal): Promise<T> {
    return this.get<T>(
      `/v1/bookmakers?lang=${encodeURIComponent(lang)}&context=${encodeURIComponent(context)}`,
      signal,
    )
  }

  /** Football match center (fixtures + 1X2) from betbrothers v2 via the hub. */
  footballMatches<T = unknown>(leagueId: string, lang: string, signal?: AbortSignal): Promise<T> {
    return this.get<T>(
      `/v1/football/leagues/${encodeURIComponent(leagueId)}/matches?lang=${encodeURIComponent(contentLang(lang))}`,
      signal,
    )
  }

  /** Basketball match center (fixtures) from the betbrothers-basket API via the hub. */
  /**
   * Several leagues in one windowed response (the combined centres). The API
   * merges the same per-league feeds it serves singly and tags each match with
   * its competition — one request instead of ten, and no last-season ballast.
   */
  footballMatchesCombined<T = unknown>(
    leagueIds: string[],
    lang: string,
    signal?: AbortSignal,
  ): Promise<T> {
    return this.get<T>(
      `/v1/football/matches?leagues=${encodeURIComponent(leagueIds.join(','))}&lang=${encodeURIComponent(contentLang(lang))}`,
      signal,
    )
  }

  /** Combined basketball centre — see footballMatchesCombined. */
  basketballMatchesCombined<T = unknown>(
    leagueIds: string[],
    signal?: AbortSignal,
  ): Promise<T> {
    return this.get<T>(
      `/v1/basketball/matches?leagues=${encodeURIComponent(leagueIds.join(','))}`,
      signal,
    )
  }

  basketballMatches<T = unknown>(leagueId: string, signal?: AbortSignal): Promise<T> {
    return this.get<T>(
      `/v1/basketball/leagues/${encodeURIComponent(leagueId)}/matches`,
      signal,
    )
  }

  /** Tennis 2-way odds map by match id (home/away), from the hub's cache. */
  tennisOdds<T = unknown>(signal?: AbortSignal): Promise<T> {
    return this.get<T>('/v1/tennis/match-odds', signal)
  }

  /** 2-way odds map by match id for a getodds category (basket_10, hockey_10 …). */
  matchOdds<T = unknown>(cat: string, signal?: AbortSignal): Promise<T> {
    return this.get<T>(`/v1/match-odds/${encodeURIComponent(cat)}`, signal)
  }

  /**
   * Combined odds for a batch of matches: GoalServe by id + Tonybet/Cloudbet by
   * name for the gaps. POST because it carries the match list.
   */
  async resolveOdds<T = unknown>(
    cat: string,
    matches: { id: string; home: string; away: string; dateUtc?: string }[],
    signal?: AbortSignal,
  ): Promise<T> {
    const response = await fetch(`${this.baseUrl}/v1/odds/resolve`, {
      method: 'POST',
      signal,
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({ cat, matches }),
    })
    if (!response.ok) {
      throw new SportsApiError(`resolveOdds failed with ${response.status}`, response.status)
    }
    return (await response.json()) as T
  }
}
